import Api from 'services/api';
const { findBranch } = require('./findBranch');
const { dataQualityWarning } = require('./dataQualityWarning');

const formatDate = date => {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${year}-${month}-${day}`;
};

const gpStaffHeaders = [
    'id',
    'org_code',
    'org_name',
    'created_at',
    'updated_at',
    'home-page-staff-type',
    'first-name',
    'last-name',
    'home-page-staff-group-gp',
    'staff-role',
    'leavers',
    'status',
    'dq_warning'
];

const gpStaffLabels = {
    id: 'Submission ID',
    org_code: 'Organisation Code',
    org_name: 'Organisation Name',
    created_at: 'Submission Created At',
    updated_at: 'Submission Updated At',
    'home-page-staff-type': 'Staff Type',
    'first-name': 'First Name',
    'last-name': 'Last Name',
    'home-page-staff-group-gp': 'Staff Group',
    'staff-role': 'Staff Role',
    leavers: 'Active',
    status: 'Status',
    dq_warning: 'Data Quality Warning'
};

const pcnStaffHeaders = [
    'id',
    'org_code',
    'org_name',
    'created_at',
    'updated_at',
    'first-name',
    'last-name',
    'home-page-staff-group-pcn',
    'staff-role',
    'leavers',
    'status',
    'dq_warning'
];

const pcnStaffLabels = {
    id: 'Submission ID',
    org_code: 'Organisation Code',
    org_name: 'Organisation Name',
    created_at: 'Submission Created At',
    updated_at: 'Submission Updated At',
    'first-name': 'First Name',
    'last-name': 'Last Name',
    'home-page-staff-group-pcn': 'Staff Group',
    'staff-role': 'Staff Role',
    leavers: 'Active',
    status: 'Status',
    dq_warning: 'Data Quality Warning'
};

const headerConfig = {
    'PCN Contracted Services': {
        headers: [
            'id',
            'org_code',
            'org_name',
            'created_at',
            'updated_at',
            'organisation-providing-service',
            'staff-group',
            'staff-role',
            'average-hours',
            'status',
            'dq_warning'
        ],
        labels: {
            id: 'Submission ID',
            org_code: 'Organisation Code',
            org_name: 'Organisation Name',
            created_at: 'Submission Created At',
            updated_at: 'Submission Updated At',
            'organisation-providing-service': 'Organisation Providing Service',
            'staff-group': 'Staff Group',
            'staff-role': 'Staff Role',
            'average-hours': 'Average Hours',
            status: 'Status',
            dq_warning: 'Data Quality Warning'
        }
    },
    'NHS Workforce GP Practice Staff Details': {
        headers: gpStaffHeaders,
        labels: gpStaffLabels
    },
    'NHS Workforce PCN Staff Details': {
        headers: pcnStaffHeaders,
        labels: pcnStaffLabels
    }
};

/**
 * Export data to CSV and trigger download. Data is formatted.
 * @param {Array} submissionsData - Fresh data to export
 * @param {Array} submissionsDataCache - Cached data if fresh one is empty
 */
export const exportFormattedCsv = (submissionsData, submissionsDataCache) => {
    const dataToExport =
        submissionsData && submissionsData.length > 0
            ? submissionsData
            : submissionsDataCache || [];

    if (!dataToExport || dataToExport.length === 0) {
        return;
    }

    const orgData = dataToExport[0]?.org_name || '';
    const codePart = orgData.split(' - ')[1] || 'unknown-code';
    const defaultFilename = formatFileName(dataToExport[0], 'format');

    const collectionID = sessionStorage.getItem('collectionID');
    const selectedConfig = headerConfig[collectionID] || {
        headers: gpStaffHeaders,
        labels: gpStaffLabels
    };

    const headers = selectedConfig.headers;
    const headerLabelMap = selectedConfig.labels;

    let csvContent = headers.map(h => `"${headerLabelMap[h] || h}"`).join(',') + '\n';

    dataToExport.forEach(row => {
        const rowContent = headers
            .map(field => {
                const val = row[field];
                if (val && typeof val === 'object' && 'value' in val) {
                    return `"${String(val.value).replace(/"/g, '""')}"`;
                }
                const safeVal = val !== undefined && val !== null ? String(val) : '';
                return `"${safeVal.replace(/"/g, '""')}"`;
            })
            .join(',');
        csvContent += rowContent + '\n';
    });

    downloadCSV(csvContent, defaultFilename, codePart, collectionID, 'CSV Export - Formatted');
};

/**
 * Exports a csv version of the full data of all the submissions for an org in a specific collection
 * @param {Object} submissionData - All the submission data for an org in a specific collection
 */
export const exportAllCsv = async submissionsData => {
    const dataToExport = submissionsData.data.submissions;
    const orgData = dataToExport[0]?.org_name || '';
    const codePart = orgData.split(' - ')[1] || 'unknown-code';
    const defaultFilename = formatFileName(dataToExport[0], 'all');

    const idToSlugMap = {
        '7ba115a2-0848-4cea-87e6-018eb4555966': 'nhs-workforce-staff-details',
        'a9cb7ff4-eb1d-48ab-a064-4cba70f108z8': 'nhs-workforce-pcn-staff-details',
        '67e6c1e5-a7da-4848-a5bc-6480dfdzz7d6': 'nhs-workforce-pcn-pooled-roles'
    };

    var headers = [
        'id',
        'org_code',
        'org_name',
        'created_at',
        'updated_at',
        'organisation-providing-service',
        'status',
        'dq_warning'
    ];

    // Collects a copy of the form that is having its data exported so it can collect header fields
    const api = new Api();
    const collectionData = await api.getCollection(idToSlugMap[dataToExport[0]?.collection_id]);
    const form = collectionData.data.form.sections;

    form.forEach(section => {
        extractAllIds(section.questions, headers);
    });

    dataToExport.forEach(submission => {
        submission.dq_warning = checkDQWarning(submission, form);
    });

    const csvData = [
        headers.map(formatHeader),
        ...dataToExport.map(sub => extractCsvRow(sub, headers))
    ];
    downloadCSV(
        toCsvString(csvData),
        defaultFilename,
        codePart,
        collectionData.data.name,
        'CSV Export - All'
    );
};

/**
 * Converts a submission object into an ordered array of its components
 * @param {Object} entry - The submission record being converted into an ordered array
 * @param {Array} headers - The headers used for formatting the order of the fields
 */
function extractCsvRow(entry, headers) {
    const base = {
        id: entry.id || '',
        org_code: entry.org_code || '',
        org_name: entry.org_name || '',
        created_at: entry.created_at || '',
        updated_at: entry.updated_at || '',
        status: entry.status || '',
        dq_warning: entry.dq_warning || ''
    };

    const questionMap = {};
    entry.in_progress?.sections?.forEach(section => {
        section.questions?.forEach(q => {
            questionMap[q.id] = q.value || '';
        });
    });

    const row = headers.map(header => {
        if (Object.prototype.hasOwnProperty.call(base, header)) return base[header];
        if (Object.prototype.hasOwnProperty.call(questionMap, header)) return questionMap[header];
        return '';
    });

    return row;
}

/**
 * Using the first submission it formats a file name for the csv
 * @param {String} dataToExport - Submission data used to format the file name
 */
function formatFileName(dataToExport, extractType) {
    const orgName = dataToExport?.org_name || '';
    const orgCode = dataToExport?.org_code || '';
    const formattedOrgName = orgName.replace(/ /g, '');

    const defaultFilename = `${
        extractType === 'all' ? 'FullExtract' : 'Filtered'
    }_${formattedOrgName}_${orgCode}_${formatDate(new Date())}.csv`;
    return defaultFilename;
}

/**
 * Converts CSV data into a blob and downloads to the users local enviornment
 * @param {Array} csvContent - Formatted CSV data
 * @param {String} fileName - The given file name for the CSV
 * @param {String} codePart - The org code of the org requesting the extract
 * @param {String} collectionID - The collection ID of the collection being extracted
 */
function downloadCSV(csvContent, fileName, codePart, collectionID, auditMessage) {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    const api = new Api();
    api.postAuditLog(codePart, collectionID, auditMessage);
}

/**
 * Converts an array of objects into a CSV string
 * @param {String} header - Header fields for the CSV file
 */
function toCsvString(data) {
    return data
        .map(row =>
            row
                .map(field => {
                    if (field === null || field === undefined) return '';
                    const str = String(field).replace(/"/g, '""'); // escape double quotes
                    return `"${str}"`; // wrap in quotes to handle commas/newlines
                })
                .join(',')
        )
        .join('\n');
}

/**
 * Converts the CSV data into a blob and downloads to the users local enviornment
 * @param {String} header - Header fields for the CSV file
 */
function formatHeader(header) {
    return header
        .replace(/-/g, ' ') // swap hyphens for spaces
        .replace(/_/g, ' ') //  swap underscores for spaces
        .replace(/^./, c => c.toUpperCase()); // capitalise first letter
}

function extractAllIds(questions = [], collected = []) {
    questions.forEach(question => {
        if (question.id && question.type !== 'text-block') {
            if (question.id === 'leavers') {
                collected.push('Active');
            } else {
                collected.push(question.id);
            }
        }

        if (Array.isArray(question.options)) {
            question.options.forEach(option => {
                if (Array.isArray(option.questions)) {
                    extractAllIds(option.questions, collected);
                }
            });
        }
    });

    return collected;
}

function checkDQWarning(data, collectionSection) {
    const branches = findBranch(data.in_progress, collectionSection);
    return dataQualityWarning(collectionSection, data.in_progress.sections, branches);
}
